import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { profileStorageKey } from './profileStorageKey';

const PROFILE_WALLET_CREDITS_PREFIX = 'ofrontend.profileWalletCredits';

/** In-memory fallback when localStorage is unavailable (tests / private mode). */
const memoryStore = new Map<string, string>();

function safeGet(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key);
    }
  } catch {
    /* fall through */
  }
  return memoryStore.get(key) ?? null;
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
      return;
    }
  } catch {
    /* fall through */
  }
  memoryStore.set(key, value);
}

function safeRemove(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
  memoryStore.delete(key);
}

function resolvedAccountId(loginId?: string | null): string | null {
  const id = (loginId ?? getStoredLoginIdentifier())?.trim().toLowerCase();
  return id || null;
}

/** Last known private-client wallet balance (survives refresh when GET omits remaining_credits). */
export function getStoredProfileWalletCredits(loginId?: string | null): number | null {
  const account = resolvedAccountId(loginId);
  if (!account) {
    return null;
  }
  const raw = safeGet(profileStorageKey(PROFILE_WALLET_CREDITS_PREFIX, account));
  if (!raw?.trim()) {
    return null;
  }
  const parsed = Number(raw.trim());
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function persistProfileWalletCredits(credits: number, loginId?: string | null): void {
  const account = resolvedAccountId(loginId);
  if (!account || !Number.isFinite(credits) || credits < 0) {
    return;
  }
  safeSet(profileStorageKey(PROFILE_WALLET_CREDITS_PREFIX, account), String(Math.floor(credits)));
}

export function clearStoredProfileWalletCredits(loginId?: string | null): void {
  const account = resolvedAccountId(loginId);
  if (!account) {
    return;
  }
  safeRemove(profileStorageKey(PROFILE_WALLET_CREDITS_PREFIX, account));
}

/**
 * Prefer an explicit API wallet balance. When the profile payload has no
 * `remaining_credits` / `remainingCredits` field, keep the last persisted balance.
 */
export function resolvePrivateClientWalletCredits(options: {
  apiCredits: number | null;
  apiHasRemainingCreditsField: boolean;
  loginId?: string | null;
}): number {
  const { apiCredits, apiHasRemainingCreditsField, loginId } = options;
  if (apiHasRemainingCreditsField && apiCredits !== null && Number.isFinite(apiCredits)) {
    persistProfileWalletCredits(apiCredits, loginId);
    return apiCredits;
  }
  const stored = getStoredProfileWalletCredits(loginId);
  if (stored !== null) {
    return stored;
  }
  if (apiCredits !== null && Number.isFinite(apiCredits)) {
    return apiCredits;
  }
  return 0;
}
